import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { 
  Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, Chip, IconButton, CircularProgress, Paper, Typography,
  Accordion, AccordionSummary, AccordionDetails, Box, Dialog, DialogTitle, DialogContent,
  Card, CardMedia
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import {
  createFacilityProject, updateFacilityProject, getFacilityProjectById,
  getJoinedGreenUsers, uploadFacilityProjectAttachment, deleteFacilityProjectAttachment,
  setStoreData, downloadFacilityProjectAttachment, getAllFacilities, getProjectTaskTags
} from '../../Redux/actions';
import { selectFocusedStyle, buttonStyles, datePickerSlotProps } from '../../theme/formStyles';

const themedTextFieldSx = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#008080' }
};

const themedAutocompleteSx = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#008080' }
};

const TASK_OPTIONS = [
  {
    name: 'Material Procurement & Delivery',
    subTasks: [
      'Material Procurement',
      'Material Delivery to Site',
      'Consumables Procurement (Client Scope)',
    ],
  },
  {
    name: 'Project Execution',
    subTasks: [
      'OCEMS Installation',
      'Sampling Line Installation',
      'Energy Meter Installation',
      'Flowmeter Installation',
      'Level Sensor Installation',
      'Equipment Status Integration',
      'Cable Lying Activity',
      'AutoChlorination Installation',
      'Chillmax Installation',
      'Quality Meters Installation',
      'DO Meter Installation',
    ],
  },
  {
    name: 'Automation Activity',
    subTasks: [
      'STP/SBR Matic Installation',
      'Float Switch Installation',
      'Automatic Logic Update',
    ],
  },
  {
    name: 'Commissioning and Handover',
    subTasks: [
      'Testing & Commissioning',
      'Dashboard Training',
      'Project Handover',
    ],
  },
];

export default function ManageProjectManagement() {
  const { client_id, id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [greenUsers, setGreenUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [taskTags, setTaskTags] = useState([]);

  const [form, setForm] = useState({
    facility_id: '',
    project_owner: [],
    description: '',
    project_budget: '',
  });

  const [tasks, setTasks] = useState([
    {
      task_name: '',
      sub_tasks: [] 
    }
  ]);

  const [errors, setErrors] = useState({});
  const [deletedAttachments, setDeletedAttachments] = useState([]);
  const [uploadingSubTaskId, setUploadingSubTaskId] = useState(null);
  const [previewModal, setPreviewModal] = useState({ open: false, attachment: null });

  const statusOptions = ['Pending', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];

  useEffect(() => {
    fetchFacilities();
    fetchTaskTags();
    if (id) {
      fetchProject();
    }
  }, [id, client_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchGreenUsers();
  }, [client_id, form.facility_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGreenUsers = () => {
    dispatch(getJoinedGreenUsers()).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        let users = res.data.allData || res.data.data || [];
        users = users.filter((u) => {
          const email = (u.user_id || '').toLowerCase();
          return email.includes('@greenvironment');
        });
        setGreenUsers(users);
      }
    });
  };

  const fetchFacilities = () => {
    dispatch(getAllFacilities(client_id, {})).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        setFacilities(res.data.data || []);
      }
    });
  };

  const fetchTaskTags = () => {
    dispatch(getProjectTaskTags()).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        setTaskTags(res.data.data || []);
      }
    });
  };

  // Predefined TASK_OPTIONS are always offered, merged with names/sub-names learned
  // from previously saved projects (via the task-tags endpoint) so manual entries
  // become reusable suggestions without losing the built-in defaults.
  const taskNameOptions = React.useMemo(() => {
    const names = new Set(TASK_OPTIONS.map(t => t.name));
    taskTags.forEach(t => { if (t.task_name) names.add(t.task_name); });
    return Array.from(names);
  }, [taskTags]);

  const getSubTaskOptions = (taskName) => {
    const names = new Set(TASK_OPTIONS.find(t => t.name === taskName)?.subTasks || []);
    const learned = taskTags.find(t => t.task_name === taskName)?.sub_task_names || [];
    learned.forEach(n => names.add(n));
    return Array.from(names);
  };

  const fetchProject = () => {
    setLoading(true);
    dispatch(getFacilityProjectById(id)).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        const data = res.data.data;
        setForm({
          facility_id: data.facility_id || '',
          project_owner: data.project_owner || [],
          project_budget: data.project_budget || '',
          description: data.description || '',
          status: data.status || 'ACTIVE'
        });
        // Re-fetch users if facility changed
        if (data.facility_id) {
          fetchGreenUsers();
        }
        
        if (data.tasks && data.tasks.length > 0) {
          const formattedTasks = data.tasks.map(task => ({
            id: task.id,
            task_name: task.task_name || '',
            sub_tasks: (task.sub_tasks || []).map(st => ({
              id: st.id,
              sub_task_name: st.sub_task_name || '',
              start_date: st.start_date || '',
              end_date: st.end_date || '',
              description: st.description || '',
              spoc: st.spoc || '',
              status: st.status || 'Pending',
              attachments: st.attachments || [] 
            }))
          }));
          setTasks(formattedTasks);
        }
      }
      setLoading(false);
    });
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  const addTask = () => {
    setTasks([...tasks, { task_name: '', sub_tasks: [] }]);
  };

  const removeTask = (index) => {
    const updated = [...tasks];
    updated.splice(index, 1);
    setTasks(updated);
  };

  const handleTaskNameChange = (taskIndex, value) => {
    const updated = [...tasks];
    updated[taskIndex].task_name = value;
    updated[taskIndex].sub_tasks = updated[taskIndex].sub_tasks.map(st => ({ ...st, sub_task_name: '' }));
    setTasks(updated);
    if (errors.tasks?.[taskIndex]?.task_name) {
      const newErrors = { ...errors, tasks: [...(errors.tasks || [])] };
      newErrors.tasks[taskIndex] = { ...newErrors.tasks[taskIndex] };
      delete newErrors.tasks[taskIndex].task_name;
      setErrors(newErrors);
    }
  };

  const addSubTask = (taskIndex) => {
    const updated = [...tasks];
    updated[taskIndex].sub_tasks.push({
      sub_task_name: '', start_date: '', end_date: '', description: '', spoc: '', status: 'Pending', attachments: []
    });
    setTasks(updated);
  };

  const removeSubTask = (taskIndex, subTaskIndex) => {
    const updated = [...tasks];
    updated[taskIndex].sub_tasks.splice(subTaskIndex, 1);
    setTasks(updated);
  };

  const updateSubTask = (taskIndex, subTaskIndex, field, value) => {
    const updated = [...tasks];
    updated[taskIndex].sub_tasks[subTaskIndex][field] = value;
    setTasks(updated);
    if (errors.tasks?.[taskIndex]?.sub_tasks?.[subTaskIndex]?.[field]) {
      const newErrors = { ...errors, tasks: [...(errors.tasks || [])] };
      newErrors.tasks[taskIndex] = { ...newErrors.tasks[taskIndex], sub_tasks: [...(newErrors.tasks[taskIndex].sub_tasks || [])] };
      newErrors.tasks[taskIndex].sub_tasks[subTaskIndex] = { ...newErrors.tasks[taskIndex].sub_tasks[subTaskIndex] };
      delete newErrors.tasks[taskIndex].sub_tasks[subTaskIndex][field];
      setErrors(newErrors);
    }
  };

  const handleFileUpload = (taskIndex, subTaskIndex, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newFiles = files.map(file => ({
      file,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      preview: URL.createObjectURL(file),
      isNew: true
    }));

    const updated = [...tasks];
    updated[taskIndex].sub_tasks[subTaskIndex].attachments = [
      ...updated[taskIndex].sub_tasks[subTaskIndex].attachments,
      ...newFiles
    ];
    setTasks(updated);
  };

  const removeAttachment = (taskIndex, subTaskIndex, attIndex) => {
    const updated = [...tasks];
    const attachment = updated[taskIndex].sub_tasks[subTaskIndex].attachments[attIndex];
    
    if (!attachment.isNew && attachment.id) {
      setDeletedAttachments([...deletedAttachments, attachment.id]);
    }
    
    updated[taskIndex].sub_tasks[subTaskIndex].attachments.splice(attIndex, 1);
    setTasks(updated);
  };

  const handlePreview = async (attachment) => {
    if (attachment.isNew) {
      setPreviewModal({ open: true, attachment: { ...attachment, url: attachment.preview } });
    } else {
      if (attachment.file_path) {
        setPreviewModal({ open: true, attachment: { ...attachment, url: attachment.file_path } });
      } else {
        dispatch(downloadFacilityProjectAttachment(attachment.id)).then(res => {
          if (res?.status === 200 && res.data?.success) {
            setPreviewModal({ open: true, attachment: { ...attachment, url: res.data.data.url } });
          }
        });
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.facility_id) newErrors.facility_id = 'Facility is required';

    const tasksErrors = [];
    tasks.forEach((task, tIdx) => {
      const tErr = {};
      if (!task.task_name) tErr.task_name = 'Task Name is required';
      
      const stErrs = [];
      task.sub_tasks.forEach((st, stIdx) => {
        const sErr = {};
        if (!st.sub_task_name) sErr.sub_task_name = 'Sub-task Name is required';
        if (!st.start_date) sErr.start_date = 'Start Date is required';
        if (!st.end_date) sErr.end_date = 'End Date is required';
        else if (st.start_date && dayjs(st.end_date).isBefore(dayjs(st.start_date), 'day')) sErr.end_date = 'End Date must be on or after Start Date';
        if (Object.keys(sErr).length > 0) stErrs[stIdx] = sErr;
      });
      if (stErrs.length > 0) tErr.sub_tasks = stErrs;
      
      if (Object.keys(tErr).length > 0) tasksErrors[tIdx] = tErr;
    });

    if (tasksErrors.length > 0) newErrors.tasks = tasksErrors;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      if (deletedAttachments.length > 0 && id) {
        for (const attId of deletedAttachments) {
          await dispatch(deleteFacilityProjectAttachment(attId));
        }
      }

      const payloadTasks = JSON.parse(JSON.stringify(tasks));
      let subTaskToFilesMapping = []; 

      payloadTasks.forEach((pt, tIdx) => {
        pt.sub_tasks.forEach((st, stIdx) => {
          const newFiles = tasks[tIdx].sub_tasks[stIdx].attachments.filter(a => a.isNew);
          if (newFiles.length > 0) {
            subTaskToFilesMapping.push({ tIdx, stIdx, newFiles });
          }
          delete st.attachments;
        });
      });

      const payload = {
        client_id,
        facility_id: form.facility_id,
        project_owner: form.project_owner,
        description: form.description,
        project_budget: form.project_budget ? parseFloat(form.project_budget) : null,
        tasks: payloadTasks,
      };

      let savedDataResponse = null;
      if (id) {
        savedDataResponse = await dispatch(updateFacilityProject(id, payload));
      } else {
        savedDataResponse = await dispatch(createFacilityProject(payload));
      }

      if (savedDataResponse.data?.success || savedDataResponse.status === 200 || savedDataResponse.status === 201) {
        const freshProject = savedDataResponse.data.data;

        if (subTaskToFilesMapping.length > 0 && freshProject?.tasks) {
          for (const mapping of subTaskToFilesMapping) {
            const serverTask = freshProject.tasks[mapping.tIdx];
            const serverSubTask = serverTask?.sub_tasks[mapping.stIdx];

            if (serverSubTask) {
              setUploadingSubTaskId(serverSubTask.id);
              for (const aFile of mapping.newFiles) {
                const formData = new FormData();
                formData.append('file', aFile.file);
                formData.append('stage', 'attachment'); 
                await dispatch(uploadFacilityProjectAttachment(serverSubTask.id, formData));
              }
            }
          }
        }

        dispatch(setStoreData('alertMessage', {
          msg: savedDataResponse.data?.message || `Project ${id ? 'updated' : 'created'} successfully`,
          type: 'success',
          handleClose: () => {
             dispatch(setStoreData('alertMessage', null));
             navigate(`/client/${client_id}/settings/project_management`);
          }
        }));
      } else {
        dispatch(setStoreData('alertMessage', { type: 'fail', msg: savedDataResponse.data?.message || 'Failed to save project' }));
      }
    } catch (err) {
      dispatch(setStoreData('alertMessage', { type: 'fail', msg: 'An unexpected error occurred' }));
    } finally {
      setSubmitting(false);
      setUploadingSubTaskId(null);
    }
  };

  const handleBack = () => {
    navigate(`/client/${client_id}/settings/project_management`);
  };

  const renderFilePreview = (attachment) => {
    const isImage = attachment.file_type?.startsWith('image/');
    const isPDF = attachment.file_type === 'application/pdf';

    if (isImage) {
      return (
        <CardMedia
          component="img"
          height="100"
          image={attachment.url || attachment.preview || attachment.file_path}
          alt={attachment.file_name}
          sx={{ objectFit: 'cover' }}
        />
      );
    } else if (isPDF) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100, bgcolor: '#f5f5f5' }}>
          <InsertDriveFileIcon sx={{ fontSize: 48, color: '#d32f2f' }} />
        </Box>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100, bgcolor: '#f5f5f5' }}>
          <InsertDriveFileIcon sx={{ fontSize: 48, color: '#666' }} />
        </Box>
      );
    }
  };

  const ownerOptions = greenUsers;
  
  if (loading && id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography variant="h6">
            {id ? 'Edit Project' : 'Create Project'}
          </Typography>
        </Box>

        <Paper sx={{ padding: 3 }}>
          <Grid container spacing={3}>
            {/* Facility Name */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="medium" sx={themedTextFieldSx} error={!!errors.facility_id}>
                <InputLabel sx={{ '&.Mui-focused': { color: '#008080' } }}>Facility Name *</InputLabel>
                <Select
                  value={form.facility_id}
                  label="Facility Name *"
                  onChange={(e) => handleChange('facility_id', e.target.value)}
                  sx={selectFocusedStyle}
                >
                  {facilities.map(fac => (
                    <MenuItem key={fac.id} value={fac.id}>{fac.facility_name}</MenuItem>
                  ))}
                </Select>
                {errors.facility_id && <Typography color="error" variant="caption">{errors.facility_id}</Typography>}
              </FormControl>
            </Grid>

            {/* Project Budget */}
            <Grid item xs={12} md={4}>
              <TextField 
                type="number"
                fullWidth 
                label="Project Budget" 
                value={form.project_budget} 
                onChange={(e) => handleChange('project_budget', e.target.value)}
                sx={themedTextFieldSx}
              />
            </Grid>

            {/* Project Owner / SPOC */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                multiple
                options={ownerOptions}
                getOptionLabel={(option) => (option.name || option.label) ? (option.user_id ? `${option.name || option.label} (${option.user_id})` : (option.name || option.label)) : ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={form.project_owner || []}
                onChange={(e, val) => handleChange('project_owner', val)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Project Owner / SPOC" 
                    variant="outlined" 
                    sx={themedAutocompleteSx} 
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip 
                      key={index}
                      label={(option.name || option.label) ? (option.user_id ? `${option.name || option.label} (${option.user_id})` : (option.name || option.label)) : ''} 
                      {...getTagProps({ index })} 
                      size="small"
                      sx={{ backgroundColor: '#008080', color: '#fff', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }} 
                    />
                  ))
                }
              />
            </Grid>

            {/* Project Description */}
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Description of the Project" 
                multiline 
                rows={2} 
                value={form.description} 
                onChange={(e) => handleChange('description', e.target.value)}
                sx={themedTextFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="subtitle1" sx={{ color: '#008080', fontWeight: 600 }}>Project Tasks</Typography>
                <Button 
                  startIcon={<AddIcon />} 
                  variant="outlined" 
                  onClick={addTask}
                  sx={buttonStyles.outlined}
                >
                  Add Task
                </Button>
              </Box>

              {tasks.map((task, tIdx) => (
                <Accordion key={tIdx} defaultExpanded sx={{ mb: 2, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#008080' }} />} sx={{ bgcolor: '#f5fafa' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography sx={{ fontWeight: 600, mr: 2 }}>
                        {`Task ${tIdx + 1}: ${task.task_name || 'New Task'}`}
                      </Typography>
                      {errors.tasks && errors.tasks[tIdx]?.task_name && (
                        <Typography color="error" variant="caption">({errors.tasks[tIdx].task_name})</Typography>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ borderTop: '1px solid #eee' }}>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={11}>
                        <Autocomplete
                          freeSolo
                          options={taskNameOptions}
                          value={task.task_name}
                          onChange={(e, newValue) => handleTaskNameChange(tIdx, newValue || '')}
                          onInputChange={(e, newInputValue, reason) => {
                            if (reason === 'input') handleTaskNameChange(tIdx, newInputValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Task Name *"
                              error={!!(errors.tasks && errors.tasks[tIdx]?.task_name)}
                              helperText={errors.tasks?.[tIdx]?.task_name}
                              sx={themedTextFieldSx}
                            />
                          )}
                          sx={themedAutocompleteSx}
                        />
                      </Grid>
                      <Grid item xs={1} display="flex" justifyContent="flex-end" alignItems="center">
                        <IconButton onClick={() => removeTask(tIdx)} color="error" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">Sub Tasks</Typography>
                        <Button 
                          size="small" 
                          startIcon={<AddIcon fontSize="small" />} 
                          onClick={() => addSubTask(tIdx)}
                          sx={{ color: '#008080' }}
                        >
                          Add Sub Task
                        </Button>
                      </Box>
                      
                      {task.sub_tasks.map((subTask, stIdx) => {
                        const stError = errors.tasks?.[tIdx]?.sub_tasks?.[stIdx] || {};
                        return (
                          <Paper key={stIdx} elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #ddd', bgcolor: '#fcfcfc' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -2, zIndex: 1, position: 'relative' }}>
                              <IconButton onClick={() => removeSubTask(tIdx, stIdx)} color="error" size="small">
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  options={getSubTaskOptions(task.task_name)}
                                  value={subTask.sub_task_name}
                                  onChange={(e, newValue) => updateSubTask(tIdx, stIdx, 'sub_task_name', newValue || '')}
                                  onInputChange={(e, newInputValue, reason) => {
                                    if (reason === 'input') updateSubTask(tIdx, stIdx, 'sub_task_name', newInputValue);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Sub Task Name *"
                                      error={!!stError.sub_task_name}
                                      helperText={stError.sub_task_name}
                                      sx={themedTextFieldSx}
                                    />
                                  )}
                                  sx={themedAutocompleteSx}
                                />
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <DatePicker
                                  label="Start Date *"
                                  format="DD/MM/YYYY"
                                  sx={{ width: '100%' }}
                                  value={subTask.start_date ? dayjs(subTask.start_date) : null}
                                  onChange={(val) => updateSubTask(tIdx, stIdx, 'start_date', val ? val.format('YYYY-MM-DD') : '')}
                                  slotProps={datePickerSlotProps({ fullWidth: true, size: 'small', error: !!stError.start_date, helperText: stError.start_date })}
                                />
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <DatePicker
                                  label="End Date *"
                                  format="DD/MM/YYYY"
                                  sx={{ width: '100%' }}
                                  minDate={subTask.start_date ? dayjs(subTask.start_date) : undefined}
                                  value={subTask.end_date ? dayjs(subTask.end_date) : null}
                                  onChange={(val) => updateSubTask(tIdx, stIdx, 'end_date', val ? val.format('YYYY-MM-DD') : '')}
                                  slotProps={datePickerSlotProps({ fullWidth: true, size: 'small', error: !!stError.end_date, helperText: stError.end_date })}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField 
                                  fullWidth 
                                  label="Description" 
                                  size="small"
                                  multiline
                                  rows={2}
                                  value={subTask.description} 
                                  onChange={(e) => updateSubTask(tIdx, stIdx, 'description', e.target.value)}
                                  sx={themedTextFieldSx}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Autocomplete
                                  size="small"
                                  options={ownerOptions}
                                  getOptionLabel={(option) => (option.name || option.label) ? (option.user_id ? `${option.name || option.label} (${option.user_id})` : (option.name || option.label)) : ''}
                                  isOptionEqualToValue={(option, value) => option.user_id === (value?.id || value)}
                                  value={(() => { try { const p = JSON.parse(subTask.spoc || '{}'); return ownerOptions.find(o => o.user_id === p.id) || null; } catch { return ownerOptions.find(o => o.name === subTask.spoc) || null; } })()}
                                  onChange={(e, val) => updateSubTask(tIdx, stIdx, 'spoc', val ? JSON.stringify({ id: val.user_id, name: val.name }) : '')}
                                  renderInput={(params) => (
                                    <TextField 
                                      {...params} 
                                      label="SPOC" 
                                      variant="outlined" 
                                      sx={themedTextFieldSx}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small" sx={themedTextFieldSx}>
                                  <InputLabel sx={{ '&.Mui-focused': { color: '#008080' } }}>Status</InputLabel>
                                  <Select
                                    label="Status"
                                    value={subTask.status}
                                    onChange={(e) => updateSubTask(tIdx, stIdx, 'status', e.target.value)}
                                    sx={selectFocusedStyle}
                                  >
                                    {statusOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              {/* Sub-task Attachments Section (Opportunity Style) */}
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderTop: '1px dashed #ccc', pt: 1, mt: 1 }}>
                                  <input
                                    accept="image/*,application/pdf,.doc,.docx"
                                    style={{ display: 'none' }}
                                    id={`file-upload-${tIdx}-${stIdx}`}
                                    type="file"
                                    multiple
                                    onChange={(e) => handleFileUpload(tIdx, stIdx, e)}
                                  />
                                  <label htmlFor={`file-upload-${tIdx}-${stIdx}`}>
                                    <Button
                                      variant="outlined"
                                      component="span"
                                      startIcon={uploadingSubTaskId === subTask.id ? <CircularProgress size={16} sx={{ color: '#008080' }} /> : <AttachFileIcon />}
                                      disabled={uploadingSubTaskId === subTask.id}
                                      size="small"
                                      sx={buttonStyles.outlined}
                                    >
                                      {uploadingSubTaskId === subTask.id ? 'Uploading...' : 'Attach Files'}
                                    </Button>
                                  </label>
                                </Box>
                                {subTask.attachments && subTask.attachments.length > 0 && (
                                  <Grid container spacing={1} sx={{ mt: 1 }}>
                                    {subTask.attachments.map((attachment, aIdx) => (
                                      <Grid item xs={6} sm={4} md={3} lg={2} key={aIdx}>
                                        <Card sx={{ position: 'relative', border: '1px solid #e0e0e0' }}>
                                          <Box onClick={() => handlePreview(attachment)} sx={{ cursor: 'pointer' }}>
                                            {renderFilePreview(attachment)}
                                          </Box>
                                          <IconButton
                                            size="small"
                                            sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                                            onClick={() => removeAttachment(tIdx, stIdx, aIdx)}
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                          <Typography variant="caption" sx={{ display: 'block', p: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {attachment.file_name}
                                          </Typography>
                                        </Card>
                                      </Grid>
                                    ))}
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          </Paper>
                        );
                      })}
                      {task.sub_tasks.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                          No sub-tasks added yet.
                        </Typography>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>

            {/* Actions */}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, pt: 3, borderTop: '1px solid #eee' }}>
              <Button variant="outlined" disabled={submitting} onClick={handleBack} sx={buttonStyles.outlined}>
                Cancel
              </Button>
              <Button variant="contained" disabled={submitting} onClick={handleSubmit} sx={buttonStyles.contained}>
                {submitting ? (uploadingSubTaskId ? 'Uploading...' : <CircularProgress size={24} color="inherit" />) : (id ? 'Update Project' : 'Submit')}
              </Button>
            </Grid>

          </Grid>
        </Paper>

        <Dialog open={previewModal.open} onClose={() => setPreviewModal({ open: false, attachment: null })} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{previewModal.attachment?.file_name}</Typography>
              <IconButton onClick={() => setPreviewModal({ open: false, attachment: null })} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: '80vh', overflow: 'hidden' }}>
            {previewModal.attachment?.file_type?.startsWith('image/') ? (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5' }}>
                <img src={previewModal.attachment.url} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </Box>
            ) : (
              <iframe src={previewModal.attachment?.url} title="preview" width="100%" height="100%" style={{ border: 'none' }} />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
